import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import {
  Mail, Phone, User, Send, Loader2, CheckCircle2, MessageSquare,
  RefreshCw, AlertCircle, Reply, Clock, Inbox,
} from "lucide-react";

const STATUS_TABS = [
  { id: "open",        ar: "جديدة",        en: "Open" },
  { id: "in_progress", ar: "قيد المعالجة", en: "In progress" },
  { id: "resolved",    ar: "مغلقة",        en: "Resolved" },
  { id: "",            ar: "الكل",         en: "All" },
];

export default function MessagesTab() {
  const { lang } = useI18n();
  const [status, setStatus] = useState("open");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const [updateBusy, setUpdateBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = status ? { status } : {};
      const { data } = await apiClient.get("/admin/contact-messages", { params });
      setItems(data.items || []);
      // keep the selection if still in list, otherwise pick the first
      if (selectedId && !data.items.find((m) => m.id === selectedId)) {
        setSelectedId(data.items[0]?.id || null);
      } else if (!selectedId && data.items.length > 0) {
        setSelectedId(data.items[0].id);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [status, selectedId]);

  useEffect(() => { load(); }, [load]);

  const selected = items.find((m) => m.id === selectedId) || null;

  const sendReply = async () => {
    if (!selected || replyText.trim().length < 2) return;
    setReplyBusy(true);
    try {
      const { data } = await apiClient.post(`/admin/contact-messages/${selected.id}/reply`, {
        body: replyText.trim(),
        mark_status: "resolved",
      });
      setItems((curr) => curr.map((m) => (m.id === data.id ? data : m)));
      setReplyText("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setReplyBusy(false);
    }
  };

  const setMsgStatus = async (newStatus) => {
    if (!selected) return;
    setUpdateBusy(true);
    try {
      const { data } = await apiClient.patch(`/admin/contact-messages/${selected.id}`, { status: newStatus });
      setItems((curr) => curr.map((m) => (m.id === data.id ? data : m)));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setUpdateBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-messages-tab">
      {/* Header + status tabs + refresh */}
      <div className="surface rounded-2xl p-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Inbox className="w-5 h-5 text-[#8a6f2e]" />
            <h2 className="font-display text-xl font-semibold">{lang === "ar" ? "صندوق رسائل تواصل معنا" : "Contact-us inbox"}</h2>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-sm text-[#8a6f2e] hover:text-[#5a4615] font-semibold"
            data-testid="messages-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {lang === "ar" ? "تحديث" : "Refresh"}
          </button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="messages-status-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.id || "all"}
              onClick={() => setStatus(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 transition-colors ${
                status === t.id
                  ? "bg-[#0a0a0a] text-[#c9a55c] border-[#c9a55c]"
                  : "bg-white text-[#3a3a3a] border-[#0a0a0a]/12 hover:border-[#c9a55c]"
              }`}
              data-testid={`messages-tab-${t.id || "all"}`}
            >
              {lang === "ar" ? t.ar : t.en}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border-2 border-red-200 bg-red-50 p-3 text-sm text-red-700 inline-flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <div className="grid lg:grid-cols-[360px_1fr] gap-4">
        {/* List */}
        <div className="surface rounded-2xl overflow-hidden" data-testid="messages-list">
          {loading ? (
            <div className="p-6 text-center text-sm text-[#6b6b6b] inline-flex items-center gap-2 justify-center w-full">
              <Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "جاري التحميل..." : "Loading..."}
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#6b6b6b]">
              <MessageSquare className="w-8 h-8 mx-auto text-[#c9a55c] mb-2 opacity-60" />
              {lang === "ar" ? "لا توجد رسائل في هذه الفئة." : "No messages in this category."}
            </div>
          ) : (
            <ul className="max-h-[640px] overflow-y-auto divide-y divide-[#0a0a0a]/5">
              {items.map((m) => {
                const isActive = m.id === selectedId;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(m.id)}
                      className={`w-full text-start px-4 py-3 transition-colors ${
                        isActive ? "bg-[#fdf9ef]" : "hover:bg-[#fafaf7]"
                      }`}
                      data-testid={`message-item-${m.id}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="font-semibold text-sm text-[#0a0a0a] truncate flex-1">{m.name}</span>
                        <StatusBadge status={m.status} lang={lang} />
                      </div>
                      <div className="text-xs text-[#6b6b6b] truncate">{m.subject || (lang === "ar" ? "بدون موضوع" : "No subject")}</div>
                      <div className="text-[11px] text-[#8a8a8a] mt-1 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {new Date(m.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
                        {Array.isArray(m.replies) && m.replies.length > 0 && (
                          <span className="ms-2 text-[#1f6f3a]">· {lang === "ar" ? `تم الرد (${m.replies.length})` : `Replied (${m.replies.length})`}</span>
                        )}
                        {m.auto_replied && (
                          <span className="ms-1 px-1.5 py-0.5 rounded-full bg-[#fdf9ef] text-[#8a6f2e] border border-[#c9a55c]/40 text-[9px] font-bold uppercase tracking-wider">
                            {lang === "ar" ? "ردّ ذكي" : "AI"}
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="surface rounded-2xl p-5 min-h-[300px]" data-testid="messages-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-[#8a8a8a] py-10">
              <Inbox className="w-12 h-12 mb-3 opacity-50" />
              <p className="text-sm">{lang === "ar" ? "اختر رسالة لعرضها والرد عليها." : "Select a message to view & reply."}</p>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="font-display text-xl font-semibold text-[#0a0a0a]">{selected.name}</div>
                  <div className="text-xs text-[#6b6b6b] mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1 hover:text-[#8a6f2e]" data-testid="msg-email">
                      <Mail className="w-3.5 h-3.5" /> {selected.email}
                    </a>
                    {selected.phone && (
                      <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-1 hover:text-[#8a6f2e]" dir="ltr">
                        <Phone className="w-3.5 h-3.5" /> {selected.phone}
                      </a>
                    )}
                    {selected.user_id && (
                      <span className="inline-flex items-center gap-1 text-[#1f6f3a]" title={lang === "ar" ? "مستخدم مسجّل" : "Registered user"}>
                        <User className="w-3.5 h-3.5" /> {lang === "ar" ? "مستخدم في كارتال" : "CARTAL user"}
                      </span>
                    )}
                  </div>
                </div>
                <StatusBadge status={selected.status} lang={lang} />
              </div>

              {selected.subject && (
                <div className="text-sm text-[#3a3a3a] font-semibold mb-2">{selected.subject}</div>
              )}
              <div className="rounded-xl border border-[#0a0a0a]/8 bg-[#fafaf7] p-4 text-sm text-[#0a0a0a] leading-relaxed whitespace-pre-wrap" data-testid="msg-body">
                {selected.message}
              </div>

              {/* Prior replies */}
              {Array.isArray(selected.replies) && selected.replies.length > 0 && (
                <div className="mt-5 space-y-3" data-testid="msg-replies">
                  <div className="overline overline-gold">{lang === "ar" ? "الردود السابقة" : "Previous replies"}</div>
                  {selected.replies.map((r) => (
                    <div key={r.id} className="rounded-xl border-2 border-[#c9a55c]/40 bg-gradient-to-br from-[#fdf9ef] to-white p-4">
                      <div className="text-[11px] text-[#8a6f2e] font-semibold mb-1 inline-flex items-center gap-1.5">
                        <Reply className="w-3.5 h-3.5" />
                        {r.admin_name} · {new Date(r.created_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}
                      </div>
                      <div className="text-sm text-[#0a0a0a] whitespace-pre-wrap">{r.body}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply composer */}
              <div className="mt-5">
                <label className="overline overline-gold mb-2 block">{lang === "ar" ? "كتابة رد" : "Write a reply"}</label>
                <textarea
                  className="input-dark"
                  rows={4}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={lang === "ar" ? "اكتب الرد للمستخدم… سيتم إرساله كإشعار داخلي إن كان مسجّلاً، أو راسله مباشرة على بريده." : "Write your reply… delivered via in-app notification to registered users."}
                  data-testid="msg-reply-text"
                />
                <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setMsgStatus(selected.status === "open" ? "in_progress" : "open")}
                      disabled={updateBusy}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#0a0a0a]/15 hover:border-[#c9a55c] transition-colors"
                      data-testid="msg-toggle-progress"
                    >
                      {selected.status === "open"
                        ? (lang === "ar" ? "ضع كقيد المعالجة" : "Mark in progress")
                        : (lang === "ar" ? "أعد فتح الرسالة" : "Re-open")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMsgStatus("resolved")}
                      disabled={updateBusy || selected.status === "resolved"}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors inline-flex items-center gap-1"
                      data-testid="msg-mark-resolved"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {lang === "ar" ? "أغلق كمعالَجة" : "Mark resolved"}
                    </button>
                    <a
                      href={`mailto:${selected.email}?subject=${encodeURIComponent("CARTAL — " + (selected.subject || ""))}`}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#0a0a0a]/15 hover:border-[#c9a55c] transition-colors inline-flex items-center gap-1"
                    >
                      <Mail className="w-3.5 h-3.5" /> {lang === "ar" ? "افتح في البريد" : "Open in email"}
                    </a>
                  </div>
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={replyBusy || replyText.trim().length < 2}
                    className="btn-primary text-sm justify-center"
                    data-testid="msg-send-reply"
                  >
                    {replyBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {lang === "ar" ? "إرسال الرد" : "Send reply"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, lang }) {
  const map = {
    open:        { ar: "جديدة",        en: "Open",        cls: "bg-red-50 text-red-700 border-red-200" },
    in_progress: { ar: "قيد المعالجة", en: "In progress", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    resolved:    { ar: "مغلقة",        en: "Resolved",    cls: "bg-green-50 text-green-700 border-green-200" },
  };
  const m = map[status] || map.open;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${m.cls}`}>
      {lang === "ar" ? m.ar : m.en}
    </span>
  );
}
